import { cloneDeep } from 'lodash-es';
import { Bookmaker, TradingType } from '@/constants';
import { AccountItem } from '@/models/account';
import { SiteAccountParamItem } from '@/models/siteAccountParam';
import { createAccountsPageHeaders } from '@/pages/admin/accounts/AccountsPageHeaders';
import { accountService } from '@/services/accountService';
import { isRequestCanceled } from '@/services/baseService';
import ErrorService from '@/services/errorService';
import { siteAccountParamsService } from '@/services/siteAccountParamsService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import { IAccountFilters, IAccountStats, IAccountUpdateResponse } from '@/types/account';
import type { ISiteAccountParamsDetailState } from '@/types/siteAccountParams';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'login', order: 'asc' as const }];

function createAccountStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<AccountItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const stats = ref<IAccountStats | undefined>(undefined);
    const paramsDetailState = ref<Record<number, ISiteAccountParamsDetailState>>({});
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IAccountFilters>({
        accountSearch: {
            value: null,
            title: 'accounts.filters.accountSearch',
            allowSaveState: true,
        },
        accountBookmakerId: {
            value: Bookmaker.Pinna,
            title: 'accounts.filters.accountBookmakerId',
            allowSaveState: true,
            disableClear: true,
        },
        accountStatus: {
            value: 'active',
            title: 'accounts.filters.accountStatus',
            allowSaveState: true,
            disableClear: true,
        },
        accountToolUserId: {
            value: null,
            title: 'accounts.filters.accountToolUserId',
            allowSaveState: true,
        },
        accountProxyId: {
            value: null,
            title: 'proxy.columns.name',
            allowSaveState: false,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.accountSearch.value = null;
        filters.accountBookmakerId.value = Bookmaker.Pinna;
        filters.accountStatus.value = 'active';
        filters.accountToolUserId.value = null;
        filters.accountProxyId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createAccountsPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'account',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<IAccountFilters> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: ['Stats'],
                requiredDictionaries: ['Agent', 'Country', 'Proxy', 'Browser', 'AuditTable'],
                sortDirectionCase: 'upper',
            };

            const response = await accountService.list(params);
            items.value = response.data.rows.map((row) => new AccountItem(row));
            total.value = response.data.total;
            stats.value = response.data.stats;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        paramsDetailState.value = {};
        total.value = 0;
        stats.value = undefined;
        options.value.page = 1;
        loading.value = false;
    }

    function getParamsDetailState(accountId: number): ISiteAccountParamsDetailState {
        paramsDetailState.value[accountId] ??= { loading: false, error: false, rows: [] };
        return paramsDetailState.value[accountId];
    }

    async function loadParams(entityId: number) {
        const state = getParamsDetailState(entityId);
        if (state.loading) return;

        state.loading = true;
        state.error = false;
        state.rows = [];

        try {
            const response = await siteAccountParamsService.listByAccountId(entityId);
            state.rows = response.data.rows.map((row) => new SiteAccountParamItem(row));
        } catch (error) {
            state.error = true;
            ErrorService.log(error);
        } finally {
            state.loading = false;
        }
    }

    async function createParam(entityId: number, param: string, value: string): Promise<boolean> {
        try {
            const item = new SiteAccountParamItem();
            item.accountId = entityId;
            item.param = param;
            item.value = value;

            const response = await siteAccountParamsService.create({
                rows: [item.toCreateApiData()],
            });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    async function updateParam(entityId: number, param: SiteAccountParamItem, fields: Array<'param' | 'value'>): Promise<boolean> {
        try {
            const response = await siteAccountParamsService.update({
                rows: [param.toUpdateApiData(fields)],
            });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    async function deleteParam(entityId: number, paramId: number): Promise<boolean> {
        try {
            const response = await siteAccountParamsService.delete({ rows: [{ id: paramId }] });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    function mergeMutationResponse(response: { data: IAccountUpdateResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (row) {
            const idx = items.value.findIndex((i) => i.id === row.id);
            if (idx !== -1) {
                items.value[idx] = new AccountItem(row);
            } else {
                items.value = [new AccountItem(row), ...items.value];
                total.value += 1;
            }
            triggerRef(items);
        }
        if (response.data.stats) stats.value = response.data.stats;
    }

    async function patchBoolField(item: AccountItem, modelKey: 'isActive' | 'isDisable' | 'usedForParser', value: 0 | 1, apiField: string) {
        const oldValue = item[modelKey];
        item[modelKey] = value;
        triggerRef(items);

        try {
            const response = await accountService.update({ rows: [{ id: item.id, [apiField]: value }], requiredData: ['Stats'] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                item[modelKey] = oldValue;
                triggerRef(items);
            }
        } catch {
            item[modelKey] = oldValue;
            triggerRef(items);
        }
    }

    async function patchPlatformToggle(item: AccountItem, tradingType: TradingType, platformId: number, value: boolean) {
        const isBot = tradingType === TradingType.Bot;
        const arr = isBot ? item.platformsBot : item.platformsOrder;
        const oldArr = [...arr];

        if (value) {
            if (!arr.includes(platformId)) arr.push(platformId);
        } else {
            const idx = arr.indexOf(platformId);
            if (idx !== -1) arr.splice(idx, 1);
        }
        triggerRef(items);

        const apiField = isBot ? 'platforms_bot' : 'platforms_order';
        try {
            const response = await accountService.update({ rows: [{ id: item.id, [apiField]: [...arr] }], requiredData: ['Stats'] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                if (isBot) item.platformsBot = oldArr;
                else item.platformsOrder = oldArr;
                triggerRef(items);
            }
        } catch {
            if (isBot) item.platformsBot = oldArr;
            else item.platformsOrder = oldArr;
            triggerRef(items);
        }
    }

    async function reloginAccount(id: number) {
        try {
            await accountService.relogin({ rows: [{ id }], requiredData: ['Stats'] });
        } catch {
            return;
        }
        await loadData();
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        stats,
        options,
        loadData,
        updateOptions,
        clearData,
        patchBoolField,
        patchPlatformToggle,
        reloginAccount,
        mergeMutationResponse,
        getParamsDetailState,
        loadParams,
        createParam,
        updateParam,
        deleteParam,
        hasAdd: true,
        hasRefresh: false,
    };
}

export function useAccountStore(storeId: string = 'account') {
    const useStore = defineStore(storeId, () => createAccountStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'account'),
    });
    return useStore();
}
